import { InjectionToken, Inject, Injectable, APP_INITIALIZER, NgModule, defineInjectable, inject } from '@angular/core';
import { isObservable, of } from 'rxjs';
import { share, tap } from 'rxjs/operators';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
import * as ɵngcc0 from '@angular/core';
const LiquidCacheConfigService = new InjectionToken('LiquidCacheConfigService');
/**
 * @param {?} liquiCacheService
 * @return {?}
 */
function LiquidCacheServiceFactory(liquiCacheService) {
    return () => liquiCacheService.loadDecorator();
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class LiquidCacheObject {
    /**
     * @param {?} key
     * @param {?} value
     * @param {?} configuration
     * @param {?} cacheService
     */
    constructor(key, value, configuration, cacheService) {
        this.expiresAt = null;
        this.lastUpdate = null;
        this._timeout = null;
        this.key = key;
        this.cacheService = cacheService;
        this.update(value, configuration);
    }
    /**
     * @param {?} type
     * @return {?}
     */
    is(type) {
        return this.configuration.objectType === type;
    }
    /**
     * @param {?} value
     * @param {?} configuration
     * @return {?}
     */
    update(value, configuration) {
        this.value = value;
        this.configuration = configuration;
        this.lastUpdate = new Date().getTime();
        this.calculateExpirationTime();
    }
    /**
     * @return {?}
     */
    remove() {
        if (this._timeout) {
            clearTimeout(this._timeout);
        }
        if (this.cacheService.cachedElements[this.key]) {
            delete this.cacheService.cachedElements[this.key];
        }
        try {
            if (localStorage.getItem(this.cacheService.defaultObjectParameters.localStoragePrefix + this.key)) {
                localStorage.removeItem(this.cacheService.defaultObjectParameters.localStoragePrefix + this.key);
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    /**
     * @return {?}
     */
    calculateExpirationTime() {
        if (this.configuration.duration && this.expiresAt === null) {
            this.expiresAt = new Date().getTime() + (this.configuration.duration * 1000);
            this.expiresIn(this.configuration.duration);
        }
    }
    /**
     * @param {?} seconds
     * @return {?}
     */
    expiresIn(seconds) {
        if (this._timeout) {
            clearTimeout(this._timeout);
        }
        if (this.cacheService) {
            this._timeout = setTimeout(() => {
                this.remove();
            }, seconds * 1000);
        }
    }
    /**
     * @param {?=} newExpiresAt
     * @return {?}
     */
    expirationCheck(newExpiresAt = null) {
        /** @type {?} */
        const currentTime = new Date().getTime();
        if (newExpiresAt) {
            this.expiresAt = newExpiresAt;
        }
        if (this.expiresAt) {
            if (this.expiresAt < currentTime) {
                this.remove();
            }
            else {
                this.expiresIn(Math.round((this.expiresAt - currentTime) / 1000));
            }
        }
    }
    /**
     * @return {?}
     */
    snapshot() {
        return {
            key: this.key,
            value: this.value,
            configuration: this.configuration,
            expiresAt: this.expiresAt,
            lastUpdate: this.lastUpdate
        };
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @enum {number} */
const LiquidCacheObjectTypes = {
    Observable: 0,
    Static: 1,
};
LiquidCacheObjectTypes[LiquidCacheObjectTypes.Observable] = 'Observable';
LiquidCacheObjectTypes[LiquidCacheObjectTypes.Static] = 'Static';
/** @enum {string} */
const LiquidCacheStorageTypes = {
    inMemory: 'inMemory',
    localStorage: 'localStorage',
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class LiquidCacheService {
    /**
     * @param {?} configuration
     */
    constructor(configuration) {
        this.cachedElements = {};
        this.defaultObjectParameters = {
            localStoragePrefix: 'ngxlc-',
            shareBetweenTabs: true,
            duration: null,
            objectType: LiquidCacheObjectTypes.Observable,
            storageType: LiquidCacheStorageTypes.inMemory
        };
        this.defaultObjectParameters = Object.assign({}, this.defaultObjectParameters, configuration);
        this.loadFromLocalStorage();
    }
    /**
     * @return {?}
     */
    loadDecorator() {
        DecoratorLiquidCacheService.cacheService = this;
    }
    /**
     * @param {?} key
     * @param {?} value
     * @param {?=} configuration
     * @return {?}
     */
    set(key, value, configuration = {}) {
        /** @type {?} */
        const objectConfiguration = Object.assign({}, JSON.parse(JSON.stringify(this.defaultObjectParameters)), configuration);
        if (this.has(key)) {
            this.getCacheObject(key).update(value, objectConfiguration);
        }
        else {
            this.cachedElements[key] = new LiquidCacheObject(key, value, objectConfiguration, this);
        }
        if (objectConfiguration.storageType === LiquidCacheStorageTypes.localStorage) {
            try {
                if (!isObservable(value)) {
                    localStorage.setItem(`${objectConfiguration.localStoragePrefix}${key}`, JSON.stringify(this.getCacheObject(key).snapshot()));
                }
            }
            catch (e) {
                console.error('LiquidCacheError', e);
                // TODO: manage errors if localStorage doesn't exist
            }
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    get(key) {
        if (this.has(key)) {
            /** @type {?} */
            const cacheObject = /** @type {?} */ (this.cachedElements[key]);
            return cacheObject.value;
        }
        return null;
    }
    /**
     * @param {?} key
     * @return {?}
     */
    getCacheObject(key) {
        return this.cachedElements[key];
    }
    /**
     * @param {?} key
     * @return {?}
     */
    remove(key) {
        /** @type {?} */
        const cacheObject = this.getCacheObject(key);
        if (cacheObject) {
            cacheObject.remove();
        }
    }
    /**
     * @return {?}
     */
    clear() {
        Object.keys(this.cachedElements).forEach(key => this.remove(key));
    }
    /**
     * @param {?} key
     * @return {?}
     */
    has(key) {
        this.checkSharedUpdates(key);
        return this.cachedElements[key] !== undefined;
    }
    /**
     * @param {?} key
     * @param {?=} type
     * @return {?}
     */
    is(key, type = LiquidCacheObjectTypes.Observable) {
        if (this.has(key)) {
            /** @type {?} */
            const cacheObject = /** @type {?} */ (this.getCacheObject(key));
            return cacheObject.is(type);
        }
        return false;
    }
    /**
     * @return {?}
     */
    loadFromLocalStorage() {
        try {
            Object.keys(localStorage)
                .filter(key => key.startsWith(this.defaultObjectParameters.localStoragePrefix))
                .forEach(key => {
                /** @type {?} */
                const snapshot = JSON.parse(localStorage.getItem(key));
                this.createCacheObjectFromSnapshot(snapshot);
            });
        }
        catch (e) {
            console.error('LiquidCacheError', e);
            // TODO: manage errors if localStorage doesn't exist
        }
    }
    /**
     * @param {?} key
     * @return {?}
     */
    checkSharedUpdates(key) {
        /** @type {?} */
        const fromStorage = localStorage.getItem(this.defaultObjectParameters.localStoragePrefix + key)
            ? /** @type {?} */ (JSON.parse(localStorage.getItem(this.defaultObjectParameters.localStoragePrefix + key))) : null;
        /** @type {?} */
        const fromMemory = this.cachedElements[key] !== undefined
            ? /** @type {?} */ (this.cachedElements[key]) : null;
        if (fromStorage && fromStorage.configuration.shareBetweenTabs) {
            if (!fromMemory || fromStorage.lastUpdate > fromMemory.lastUpdate) {
                this.createCacheObjectFromSnapshot(fromStorage);
            }
        }
    }
    /**
     * @param {?} snapshot
     * @return {?}
     */
    createCacheObjectFromSnapshot(snapshot) {
        this.cachedElements[snapshot.key] = new LiquidCacheObject(snapshot.key, snapshot.value, snapshot.configuration, this);
        this.getCacheObject(snapshot.key).expirationCheck(snapshot.expiresAt);
    }
}
LiquidCacheService.ɵfac = function LiquidCacheService_Factory(t) { return new (t || LiquidCacheService)(ɵngcc0.ɵɵinject(LiquidCacheConfigService)); };
LiquidCacheService.ɵprov = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjectable({ token: LiquidCacheService, factory: LiquidCacheService.ɵfac, providedIn: 'root' });
/** @nocollapse */
LiquidCacheService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: [LiquidCacheConfigService,] }] }
];
/** @nocollapse */ LiquidCacheService.ngInjectableDef = defineInjectable({ factory: function LiquidCacheService_Factory() { return new LiquidCacheService(inject(LiquidCacheConfigService)); }, token: LiquidCacheService, providedIn: "root" });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(LiquidCacheService, [{
        type: Injectable,
        args: [{
                providedIn: 'root'
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Inject,
                args: [LiquidCacheConfigService]
            }] }]; }, null); })();
class DecoratorLiquidCacheService {
    /**
     * @param {?} name
     * @param {?} parameters
     * @param {?} args
     * @return {?}
     */
    static parseKey(name, parameters, args) {
        /** @type {?} */
        let parsedKey = JSON.parse(JSON.stringify(name));
        /** @type {?} */
        const usedParameters = parsedKey.match(/\{(.*?)\}/g);
        if (usedParameters === null) {
            return parsedKey;
        }
        usedParameters
            .filter((v, i, a) => a.indexOf(v) === i)
            .forEach(param => {
            /** @type {?} */
            const paramIndex = parameters.indexOf(param.replace('{', '').replace('}', ''));
            if (paramIndex > -1) {
                parsedKey = parsedKey.replace(new RegExp(param, 'g'), args[paramIndex]);
            }
        });
        return parsedKey;
    }
}
DecoratorLiquidCacheService.cacheService = null;
/**
 * @param {?} key
 * @param {?=} configuration
 * @return {?}
 */
function LiquidCache(key, configuration = {}) {
    /** @type {?} */
    const getParametersArray = f => /\(\s*([^)]+?)\s*\)/.exec(f.toString())[1].split(/\s*,\s*/);
    return function (target, fkey, descriptor) {
        if (descriptor === undefined) {
            descriptor = Object.getOwnPropertyDescriptor(target, fkey);
        }
        /** @type {?} */
        const originalMethod = descriptor.value;
        descriptor.value = function () {
            /** @type {?} */
            const args = Object.assign([], arguments);
            /** @type {?} */
            let parsedKey;
            /** @type {?} */
            const functionParameters = getParametersArray(originalMethod);
            parsedKey = DecoratorLiquidCacheService.parseKey(key, functionParameters, args);
            if (DecoratorLiquidCacheService.cacheService.has(parsedKey)) {
                /** @type {?} */
                const cacheObject = DecoratorLiquidCacheService.cacheService.get(parsedKey);
                if (DecoratorLiquidCacheService.cacheService.is(parsedKey, LiquidCacheObjectTypes.Observable) && !isObservable(cacheObject)) {
                    return of(cacheObject);
                }
                return cacheObject;
            }
            /** @type {?} */
            const result = originalMethod.apply(this, args);
            if (isObservable(result)) {
                /** @type {?} */
                const cachedObservble = result.pipe(tap(results => DecoratorLiquidCacheService.cacheService.set(parsedKey, results, configuration)), share());
                DecoratorLiquidCacheService.cacheService.set(parsedKey, cachedObservble, configuration);
                return cachedObservble;
            }
            else {
                configuration.objectType = LiquidCacheObjectTypes.Static;
                DecoratorLiquidCacheService.cacheService.set(parsedKey, result, configuration);
                return result;
            }
        };
        return descriptor;
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
class NgxLiquidCacheModule {
    /**
     * @param {?=} config
     * @return {?}
     */
    static forRoot(config = {}) {
        return {
            ngModule: NgxLiquidCacheModule,
            providers: [
                {
                    provide: LiquidCacheConfigService,
                    useValue: config
                },
                LiquidCacheService,
                {
                    provide: APP_INITIALIZER,
                    useFactory: LiquidCacheServiceFactory,
                    deps: [LiquidCacheService],
                    multi: true
                }
            ]
        };
    }
}
NgxLiquidCacheModule.ɵfac = function NgxLiquidCacheModule_Factory(t) { return new (t || NgxLiquidCacheModule)(); };
NgxLiquidCacheModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: NgxLiquidCacheModule });
NgxLiquidCacheModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(NgxLiquidCacheModule, [{
        type: NgModule
    }], null, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { NgxLiquidCacheModule, LiquidCache, LiquidCacheService, LiquidCacheObjectTypes, LiquidCacheStorageTypes, LiquidCacheConfigService as ɵa, LiquidCacheServiceFactory as ɵb };

